import React, { useState } from 'react';
import { LoadingPage } from 'ensightful-koala-ui';
import { omit } from 'lodash';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import {
    Avatar,
    AvatarButton,
    Badge,
    Box,
    Divider,
    FileUpload,
    IconButton,
    ResponsiveDialog,
    Stack,
    Text,
} from 'shell/components';
import { AVATAR_COLOR_OPTIONS } from 'utils/constants';
import { uploadFile } from 'utils/file';

const EditProfileImageDialog = ({ open, onClose, form, onChange }) => {
    const { auth } = useAuth();

    const [isLoading, setIsLoading] = useState(false);

    const handleChangeFileUpload = async (files) => {
        const [file] = Array.from(files);
        setIsLoading(true);
        uploadFile(
            file,
            () => {},
            (data) => {
                onChange('profile_image', data);
                setIsLoading(false);
            },
            (e) => console.error('File upload failed.', e),
            auth.owner_account_id
        );
    };

    return (
        <ResponsiveDialog open={open} onClose={onClose}>
            <Stack spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'sh2'} weight={'semibold'}>
                        Edit Profile Image
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
                {form.profile_image ? (
                    <Box sx={{ m: 'auto' }}>
                        <Badge
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                                <CustomIconButton
                                    aria-label={'remove image'}
                                    kind={'delete'}
                                    size={'small'}
                                    variant={'circle'}
                                    onClick={() => onChange('profile_image', null)}
                                />
                            }
                            overlap={'circular'}
                        >
                            <Avatar size={'x-large'} user={form} />
                        </Badge>
                    </Box>
                ) : (
                    <>
                        {isLoading ? (
                            <LoadingPage style={{ height: '146px' }} />
                        ) : (
                            <FileUpload accept={'image/*'} onChange={handleChangeFileUpload} />
                        )}
                    </>
                )}
                <DividerBox sx={{ position: 'relative' }}>
                    <Divider spacing={2} />
                    <CustomText color={'light'} variant={'p3'}>
                        OR
                    </CustomText>
                </DividerBox>
                <GridBox>
                    {AVATAR_COLOR_OPTIONS.map(({ bgColor, textColor }) => (
                        <AvatarButton
                            key={bgColor}
                            selected={
                                form.profile_circle?.bg_color === bgColor &&
                                form.profile_circle?.text_color === textColor
                            }
                            size={'medium'}
                            user={{
                                ...omit(form, ['profile_image']),
                                profile_circle: { bg_color: bgColor, text_color: textColor },
                            }}
                            value={{ bgColor, textColor }}
                            onClick={({ bgColor, textColor }) =>
                                onChange('profile_circle', { bg_color: bgColor, text_color: textColor })
                            }
                        />
                    ))}
                </GridBox>
            </Stack>
        </ResponsiveDialog>
    );
};

const CustomIconButton = styled(IconButton)(({ theme }) => {
    return {
        border: `3px solid ${theme.palette.ensightful.shades[0]}`,
    };
});

const DividerBox = styled(Box)(() => {
    return {
        position: 'relative',
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        padding: theme.spacing(1),
        background: theme.palette.ensightful.shades[0],
    };
});

const GridBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gridGap: theme.spacing(4),
        justifyItems: 'center',
    };
});

export default EditProfileImageDialog;
